import { FC, useEffect, useMemo } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { featureCollection } from '@turf/helpers'
import { Box, Flex, Text } from '@fundamentals'
import { TextField, Button } from '@common/material'
import { localizedStrings } from '@core/strings'
import { useUpdateProjectMapLayer } from '@core/react-query/features/projects/maps'
import { useNavigationContext } from '@common/hooks'
import { useProjectMapConfigurator } from '@modules/geolocation'

type FeaturePropertyEditorProps = {}

interface FormData {
  [key: string]: any
}

export const FeaturePropertyEditor: FC<FeaturePropertyEditorProps> = () => {
  const { projectId } = useNavigationContext()
  const {
    drawRef,
    selectedFeature,
    updateFeatureProperties,
    setSelectedFeature,
    hasLayerGeoJsonChanges,
    setHasLayerGeoJsonChanges,
  } = useProjectMapConfigurator()
  const { control, handleSubmit, reset, formState } = useForm<FormData>({
    defaultValues: {},
  })

  const updateProjectMapLayerMutation = useUpdateProjectMapLayer()
  const isSubmittingUpdateProjectMapLayer =
    updateProjectMapLayerMutation.isLoading

  // Filter properties once using useMemo
  const filteredProperties = useMemo(() => {
    if (selectedFeature?.properties) {
      return Object.fromEntries(
        Object.entries(selectedFeature.properties).filter(
          ([key]) =>
            key !== 'id' &&
            key !== 'layerId' &&
            key !== 'ID' &&
            key !== 'styleHash' &&
            key !== 'styleMapHash' &&
            key !== 'styleUrl' &&
            key !== 'visible',
        ),
      )
    }
    return {}
  }, [selectedFeature])

  useEffect(() => {
    reset(filteredProperties)
  }, [filteredProperties])

  const onSubmit: SubmitHandler<FormData> = async (formData) => {
    if (!hasLayerGeoJsonChanges) {
      updateFeatureProperties(selectedFeature?.id?.toString(), formData)
      return
    }

    const localGeoJson = featureCollection(
      drawRef.current
        .getAll()
        .features.filter(
          (existingFeature) =>
            existingFeature.properties.layerId ===
            selectedFeature?.properties?.layerId,
        ),
    )

    await updateProjectMapLayerMutation.mutateAsync({
      projectId: projectId,
      layerId: Number(selectedFeature?.properties?.layerId),
      localGeoJson: localGeoJson,
    })

    const updatedFeature = drawRef.current?.get(selectedFeature?.id?.toString())
    setSelectedFeature(updatedFeature)

    setHasLayerGeoJsonChanges(false)
  }

  return (
    <Box>
      <Text variant='h5'>{'Feature Properties'}</Text>

      {!selectedFeature && (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          minHeight={'60dvh'}
        >
          <Text variant='h6' color={'textSecondary'}>
            {localizedStrings.selectFeatureToEditProperties}
          </Text>
        </Box>
      )}

      {!!selectedFeature && (
        <Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex flexDirection='column' p={2}>
              {Object.entries(filteredProperties).map(([key, value]) => {
                return (
                  <Controller
                    control={control}
                    key={key}
                    name={key}
                    defaultValue={value}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={key}
                        sx={{ mb: 2 }}
                        slotProps={{ inputLabel: { shrink: !!field.value } }}
                      />
                    )}
                  />
                )
              })}

              <Flex flexDirection='row' gap={2}>
                <Button
                  variant='outlined'
                  color='primary'
                  fullWidth
                  onClick={() => {
                    setSelectedFeature(null)
                  }}
                >
                  {localizedStrings.cancel}
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  type='submit'
                  fullWidth
                  disabled={
                    !!isSubmittingUpdateProjectMapLayer || !formState.isDirty
                  }
                >
                  {!hasLayerGeoJsonChanges
                    ? localizedStrings.seeChangesOnMap
                    : localizedStrings.saveToMap}
                </Button>
              </Flex>
            </Flex>
          </form>
        </Box>
      )}
    </Box>
  )
}
